import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Stepper, Step, StepLabel,
    Box, Typography, Stack, TextField, Button, Alert, Chip,
    Autocomplete, Select, MenuItem, FormControl, InputLabel,
    Switch, FormControlLabel,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import Iconify from '../Iconify';
import DentallyAvailabilityStep from './DentallyAvailabilityStep';
import DentallyBookingFormPayment from './DentallyBookingFormPayment';
import { useDentallyBooking } from '../../hooks/useDentallyBooking';
import { useSelector } from '../../redux/store';
import { parseDentallyDate } from '../../utils/dentallyUtils';
import useDentallyReasonOptions from '../../hooks/useDentallyReasonOptions';
import { callApi } from '../../utils/apiCall';
import { fetchDentallyTreatmentFee } from '../../utils/dentallyApi';

const STEPS = ['Choose Slot', 'Confirm Booking'];

/**
 * 2-step modal dialog for the follow-up "Book an appointment" Dentally flow.
 * The lead already exists — no personal details entry needed.
 *
 * Step 1: Availability slot picker (DentallyAvailabilityStep)
 * Step 2: Confirm details — slot summary, reason, assignees, notes, payment
 */
export default function DentallyFollowUpBookingDialog({
    open,
    onClose,
    existingLead,
    existingTreatmentId,
    practiceId,
    exportTrigger,
}) {
    const allUsers = useSelector((state) => state.practiceUser?.practiceUser?.results || []);
    const { reasons: reasonOptions, reasonTreatmentMap } = useDentallyReasonOptions(practiceId);

    const assignableUsers = allUsers
        .filter(
            (u) =>
                u?.is_enabled &&
                u?.role !== 'ADMIN' &&
                u?.role !== 'SUPPORT' &&
                !u?.user?.is_superuser
        )
        .map((u) => ({
            id: u.id,
            name: `${u?.user?.first_name || ''} ${u?.user?.last_name || ''}`.trim(),
        }));

    const [activeStep, setActiveStep] = useState(0);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [reason, setReason] = useState('');
    const [assignees, setAssignees] = useState([]);
    const [notes, setNotes] = useState('');
    const [paymentValues, setPaymentValues] = useState({ paymentOption: 'none' });
    const [paymentErrors] = useState({});
    const [exportTriggerOverride, setExportTriggerOverride] = useState(null);

    // Auto-populate payment amount from existing treatment price, then override with Dentally fee if available
    useEffect(() => {
        if (!practiceId || !existingTreatmentId) return;
        callApi(`/api/v1/practices/${practiceId}/treatments/${existingTreatmentId}/?fields={id,price,custom_price,dentally_treatment_id,dentally_appointment_reason}`)
            .then((r) => {
                if (r.ok) {
                    const crmPrice = r.data?.custom_price || r.data?.price || 0;
                    if (crmPrice > 0) setPaymentValues(prev => ({ ...prev, paymentAmount: crmPrice }));
                    // Auto-fill reason from synced appointment reason mapping
                    if (r.data?.dentally_appointment_reason) setReason(r.data.dentally_appointment_reason);
                    // Fetch Dentally fee and prefer it over CRM price
                    const dentallyId = r.data?.dentally_treatment_id;
                    if (dentallyId) {
                        fetchDentallyTreatmentFee(practiceId, dentallyId)
                            .then((dentallyPrice) => { if (dentallyPrice > 0) setPaymentValues(prev => ({ ...prev, paymentAmount: dentallyPrice })); })
                            .catch(() => {});
                    }
                }
            })
            .catch(() => {});
    }, [practiceId, existingTreatmentId]);

    const activeExportTrigger = exportTriggerOverride || exportTrigger;
    const isPaymentRequired = activeExportTrigger === 'PAYMENT';

    const { loading, result, error, submit, reset } = useDentallyBooking({
        practiceId,
        existingLead,
        existingTreatmentId,
        isPaymentRequired,
    });
    const canConfirm =
        selectedSlot &&
        !selectedSlot._manual &&
        (!isPaymentRequired || paymentValues.paymentOption !== 'none');

    const handleClose = () => {
        setActiveStep(0);
        setSelectedSlot(null);
        setReason('');
        setAssignees([]);
        setNotes('');
        setPaymentValues({ paymentOption: 'none' });
        setExportTriggerOverride(null);
        reset();
        onClose(false);
    };

    const handleSuccess = () => {
        onClose(true);
    };

    const handleConfirm = async () => {
        if (!selectedSlot || selectedSlot._manual) return;
        const startIso = selectedSlot.start_time;
        const finishIso = selectedSlot.finish_time;
        const durationMs = new Date(finishIso) - new Date(startIso);
        const durationMinutes = Math.round(durationMs / 60000) || 30;
        try {
            await submit({
                practitionerId: selectedSlot._queried_practitioner_id || selectedSlot.practitioner_id,
                reason,
                dateTime: new Date(startIso),
                durationMinutes,
                method: 'IN_PRACTICE',
                assignees: assignees.map((u) => u.id),
                notes,
                paymentOption: paymentValues.paymentOption || 'none',
                paymentAmount: paymentValues.paymentAmount,
                paymentType: paymentValues.paymentType || 'DEPOSIT',
                receivedMode: paymentValues.receivedMode,
            });
        } catch (e) {
            console.error('[DentallyFollowUpBookingDialog] submit error:', e);
        }
    };

    const slotLabel =
        selectedSlot && !selectedSlot._manual
            ? (parseDentallyDate(selectedSlot.start_time) || new Date(selectedSlot.start_time)).toLocaleString('en-GB', {
                  weekday: 'short',
                  day: 'numeric',
                  month: 'short',
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false,
              })
            : null;

    return (
        <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Iconify icon='medical-icon:i-dental' width={22} sx={{ color: '#5C6BC0' }} />
                Book Dentally Appointment
            </DialogTitle>

            <DialogContent>
                <Stepper activeStep={activeStep} sx={{ mb: 3, mt: 1 }}>
                    {STEPS.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                {/* ─── Step 0: Availability ─── */}
                {activeStep === 0 && (
                    <DentallyAvailabilityStep
                        practiceId={practiceId}
                        selectedSlot={selectedSlot}
                        onSelect={setSelectedSlot}
                    />
                )}

                {/* ─── Step 1: Confirm ─── */}
                {activeStep === 1 && (
                    <Box>
                        {/* Slot summary */}
                        {slotLabel && (
                            <Box
                                sx={{
                                    p: 1.5,
                                    bgcolor: 'rgba(0,171,85,0.08)',
                                    borderRadius: 1,
                                    mb: 2,
                                    border: '1px solid rgba(0,171,85,0.3)',
                                }}
                            >
                                <Stack direction='row' spacing={1} alignItems='center'>
                                    <Iconify icon='mdi:calendar-check' width={18} sx={{ color: 'success.main' }} />
                                    <Box>
                                        <Typography variant='body2' fontWeight={600}>
                                            {slotLabel}
                                        </Typography>
                                        {selectedSlot._queried_practitioner_name && (
                                            <Typography variant='caption' color='text.secondary'>
                                                {selectedSlot._queried_practitioner_name}
                                            </Typography>
                                        )}
                                    </Box>
                                </Stack>
                            </Box>
                        )}

                        {/* Appointment Reason */}
                        <FormControl fullWidth size='small' sx={{ mb: 2 }}>
                            <InputLabel>Appointment Reason</InputLabel>
                            <Select
                                value={reason}
                                label='Appointment Reason'
                                onChange={(e) => setReason(e.target.value)}
                            >
                                {reasonOptions.map((r) => (
                                    <MenuItem key={r} value={r} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Iconify icon='mdi:calendar-clock-outline' width={14} sx={{ color: reasonTreatmentMap[r] ? '#855CF8' : '#919EAB', flexShrink: 0 }} />
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography variant='body2' component='span'>{r}</Typography>
                                            {reasonTreatmentMap[r] && (
                                                <Typography variant='caption' color='text.secondary' component='span' sx={{ ml: 0.5 }}>
                                                    → {reasonTreatmentMap[r]}
                                                </Typography>
                                            )}
                                        </Box>
                                        {reasonTreatmentMap[r] && <Chip size='small' label='Linked' color='success' variant='outlined' sx={{ fontSize: 10, height: 18 }} />}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {/* Assignees */}
                        <Autocomplete
                            multiple
                            size='small'
                            sx={{ mb: 2 }}
                            options={assignableUsers}
                            getOptionLabel={(u) => u.name}
                            value={assignees}
                            onChange={(_, val) => setAssignees(val)}
                            isOptionEqualToValue={(opt, val) => opt.id === val?.id}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label='Assignees'
                                    placeholder='Search users…'
                                />
                            )}
                        />

                        {/* Notes */}
                        <TextField
                            fullWidth
                            size='small'
                            multiline
                            rows={2}
                            label='Notes (optional)'
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            sx={{ mb: 2 }}
                        />

                        {/* Payment mode toggle */}
                        <Box sx={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            mb: 2, px: 1.5, py: 1, borderRadius: 1.5,
                            bgcolor: isPaymentRequired ? 'rgba(255,171,0,0.08)' : 'rgba(0,171,85,0.06)',
                            border: '1px solid',
                            borderColor: isPaymentRequired ? 'rgba(255,171,0,0.24)' : 'rgba(0,171,85,0.16)',
                        }}>
                            <Stack direction='row' alignItems='center' spacing={1}>
                                <Iconify
                                    icon={isPaymentRequired ? 'mdi:cash-lock' : 'mdi:calendar-check'}
                                    width={18}
                                    sx={{ color: isPaymentRequired ? '#FFAB00' : '#00AB55' }}
                                />
                                <Typography variant='body2' sx={{ fontWeight: 600, fontSize: 12 }}>
                                    {isPaymentRequired ? 'Payment required before booking' : 'Book first — payment optional'}
                                </Typography>
                            </Stack>
                            <FormControlLabel
                                control={
                                    <Switch
                                        size='small'
                                        checked={isPaymentRequired}
                                        onChange={(e) => setExportTriggerOverride(e.target.checked ? 'PAYMENT' : 'APPOINTMENT')}
                                    />
                                }
                                label={<Typography sx={{ fontSize: 11, fontWeight: 600, color: 'text.secondary' }}>Require payment</Typography>}
                                sx={{ m: 0 }}
                            />
                        </Box>

                        {/* Payment */}
                        <DentallyBookingFormPayment
                            values={paymentValues}
                            onChange={(field, val) =>
                                setPaymentValues((prev) => ({ ...prev, [field]: val }))
                            }
                            errors={paymentErrors}
                        />

                        {isPaymentRequired && paymentValues.paymentOption === 'none' && (
                            <Alert severity='warning' sx={{ mt: 1 }}>
                                Payment is required by this practice before the appointment can be confirmed.
                            </Alert>
                        )}

                        {error && (
                            <Alert severity='error' sx={{ mt: 1 }}>
                                {error.message}
                            </Alert>
                        )}

                        {result?.success && (
                            <Alert severity='success' sx={{ mt: 1 }}>
                                Booking confirmed!{' '}
                                {result.dentallyWarning && `(Note: ${result.dentallyWarning})`}
                            </Alert>
                        )}
                    </Box>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose} color='inherit' disabled={loading && !result?.success}>
                    {result?.success ? 'Close' : 'Cancel'}
                </Button>

                {activeStep === 0 && (
                    <Button
                        variant='contained'
                        disabled={!selectedSlot}
                        onClick={() => setActiveStep(1)}
                        endIcon={<Iconify icon='mdi:arrow-right' width={18} />}
                    >
                        Next
                    </Button>
                )}

                {activeStep === 1 && !result?.success && (
                    <>
                        <Button
                            onClick={() => setActiveStep(0)}
                            startIcon={<Iconify icon='mdi:arrow-left' width={18} />}
                        >
                            Back
                        </Button>
                        <LoadingButton
                            variant='contained'
                            loading={loading}
                            disabled={!canConfirm}
                            onClick={handleConfirm}
                        >
                            Confirm Booking
                        </LoadingButton>
                    </>
                )}

                {result?.success && (
                    <Button variant='contained' onClick={handleSuccess}>
                        Done
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}

DentallyFollowUpBookingDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    existingLead: PropTypes.object,
    existingTreatmentId: PropTypes.number,
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    exportTrigger: PropTypes.string,
};

DentallyFollowUpBookingDialog.defaultProps = {
    existingLead: null,
    existingTreatmentId: null,
    exportTrigger: 'APPOINTMENT',
};
